/**
 * Headless presentation helpers for catalogue items — the small derivations
 * the grid tile and the configurator both need, kept platform-neutral so the
 * web shell and any future shell render the same labels.
 *
 * Icon resolution stays in the shell (it binds a concrete icon component);
 * everything here is pure data.
 */

import type { CatalogueItem } from "../data/catalogue-types";
import { CUSTOM_OPTION_SENTINEL } from "./catalogue-configuration";
import { isCountField } from "./catalogue-custom-value";

/** True when a field must be set up before the item has a price (a die, a
 *  bare panel size) — the item can't show a firm "from" price. A count
 *  (`Versions`) starts at its minimum, and a value that changes no cost (the
 *  NCR start number) has no bearing on the price, so neither counts. */
export function needsSetup(item: CatalogueItem): boolean {
  return item.fields.some(
    (f) =>
      f.mode === "always_custom" && !isCountField(f) && f.affectsCost !== false,
  );
}

export type FulfilmentTone = "made" | "bought";

export interface FulfilmentBadge {
  label: string;
  tone: FulfilmentTone;
}

/** Every part is a finished good bought whole and sold unchanged: nothing is printed or made for it. */
export function isFinishedGoods(item: Pick<CatalogueItem, "parts">): boolean {
  return item.parts.length > 0 && item.parts.every((role) => role === "goods");
}

/** The fulfilment badge for a tile: bought-in when the item is finished goods, made to order otherwise. */
export function fulfilmentBadge(
  item: Pick<CatalogueItem, "parts">,
): FulfilmentBadge {
  return isFinishedGoods(item)
    ? { label: "Bought-in", tone: "bought" }
    : { label: "Made to order", tone: "made" };
}

const SIZE_KEYWORDS: Record<string, number> = {
  a3: 297 / 420,
  a4: 210 / 297,
  a5: 148 / 210,
  a6: 105 / 148,
  dl: 210 / 99,
};

/** width/height of a size as it reads ("85 × 55 mm", "A4"), or null when it names no shape. */
function aspectOf(text: string): number | null {
  const dims = text.match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/);
  if (dims) return Number(dims[1]) / Number(dims[2]);
  const keyword = Object.keys(SIZE_KEYWORDS).find((k) =>
    text.toLowerCase().includes(k),
  );
  return keyword ? SIZE_KEYWORDS[keyword] : null;
}

/** width/height of the printed piece for the configurator's preview, from the
 *  Size field's current value. While that names no shape ("Other…" with nothing
 *  typed yet) the item's default size does, so a business card stays
 *  card-shaped; failing both, a portrait sheet. */
export function deriveAspect(
  item: Pick<CatalogueItem, "fields">,
  state: {
    values: Record<string, string | undefined>;
    custom: Record<string, string | undefined>;
  },
): number {
  const size = item.fields.find((f) => f.key === "Size");
  let text = "";
  if (size) {
    if (size.mode === "fixed") text = size.fixedValue ?? "";
    else if (size.mode === "always_custom") text = state.custom["Size"] ?? "";
    else {
      const chosen = state.values["Size"];
      if (chosen === CUSTOM_OPTION_SENTINEL) text = state.custom["Size"] ?? "";
      else text = size.options?.find((o) => o.code === chosen)?.label ?? "";
    }
  }
  const defaultSize = size?.options?.find((o) => o.isDefault)?.label;
  return (
    aspectOf(text) ??
    (defaultSize != null ? aspectOf(defaultSize) : null) ??
    210 / 297
  );
}

export type TilePrice =
  { kind: "from"; amount: number; quantity: number } | { kind: "on_setup" };

/**
 * The tile price: a firm "from" price at the default quantity, unless the
 * item has an always-custom field (size/die/variable data) — then it's
 * priced on setup, or unless no live price could be derived at all (nothing
 * cached yet, or no price group resolves).
 *
 * `alwaysCustom` lets browse-listing tiles pass the server-computed flag —
 * the listing ships quantity breaks (the rungs) but not fields, so
 * inspecting `item.fields` would wrongly read "not custom" there.
 *
 * `indicativePricePence` is `CatalogueItemTile.indicativePricePence`,
 * resolved server-side from the item's cheapest cached manufacturing cost
 * (`list_catalogue_item_tiles` -> `PricingPort.resolve_indicative_price`).
 * This function never re-derives a price from `item.quantityBreaks` itself —
 * the manual quantity-break list price is retired (99_outstanding_setup.md);
 * a caller with no indicative price (e.g. the settings catalogue editor grid,
 * which does not fetch cost-derived pricing) sees "Priced on setup" rather
 * than a stale or invented number.
 */
export function tilePrice(
  item: CatalogueItem,
  alwaysCustom: boolean = needsSetup(item),
  indicativePricePence: number | null = null,
): TilePrice {
  if (alwaysCustom || indicativePricePence == null) {
    return { kind: "on_setup" };
  }
  return {
    kind: "from",
    amount: indicativePricePence / 100,
    quantity: item.defaultQuantity,
  };
}

/** Sorted, de-duplicated standard quantities for the Quantity chip row. */
export function standardQuantities(item: CatalogueItem): number[] {
  return [...new Set(item.quantityBreaks.map((b) => b.qty))].sort(
    (a, b) => a - b,
  );
}
